import type { ScenarioContent, RunState, ExtendedCertificate } from 'shared';

const DEFAULT_HEADERS = {
  'Content-Type': 'application/json'
} as const;

export interface OpenRunResponse {
  runId: string;
  ticketStatus: string;
  scenario: {
    scenarioId: string;
    title: string;
    version: number;
    schemaVersion: number;
    content: ScenarioContent;
  };
  state: RunState;
}

export class PlayerApiClient {
  constructor(private readonly baseUrl: string = '') {}

  async openRun(ticketToken: string, deviceKey: string): Promise<OpenRunResponse> {
    const res = await this.request<{ data: OpenRunResponse }>('/api/v1/runs/open', {
      method: 'POST',
      headers: DEFAULT_HEADERS,
      body: JSON.stringify({
        ticketToken,
        deviceKey,
        client: {
          userAgent: navigator.userAgent,
          locale: navigator.language
        }
      })
    });
    return res.data;
  }

  async saveRunState(runId: string, deviceKey: string, state: RunState): Promise<void> {
    await this.request(`/api/v1/runs/${runId}`, {
      method: 'PATCH',
      headers: {
        ...DEFAULT_HEADERS,
        'X-Device-Key': deviceKey
      },
      body: JSON.stringify({ state })
    });
  }

  async sendMinigameResult(runId: string, deviceKey: string, payload: MinigameResultPayload): Promise<void> {
    await this.request(`/api/v1/runs/${runId}/minigame-result`, {
      method: 'POST',
      headers: {
        ...DEFAULT_HEADERS,
        'X-Device-Key': deviceKey
      },
      body: JSON.stringify(payload)
    });
  }

  async sendFreeInput(runId: string, deviceKey: string, payload: FreeInputPayload): Promise<FreeInputResponse> {
    const res = await this.request<{ data: FreeInputResponse }>(`/api/v1/runs/${runId}/free-input`, {
      method: 'POST',
      headers: {
        ...DEFAULT_HEADERS,
        'X-Device-Key': deviceKey
      },
      body: JSON.stringify(payload)
    });
    return res.data;
  }

  async completeRun(runId: string, deviceKey: string, endingId?: string): Promise<void> {
    await this.request(`/api/v1/runs/${runId}/complete`, {
      method: 'POST',
      headers: {
        ...DEFAULT_HEADERS,
        'X-Device-Key': deviceKey
      },
      body: JSON.stringify({ endingId })
    });
  }

  async generateCertificate(runId: string, deviceKey: string, forceRegenerate = false): Promise<ExtendedCertificate> {
    const res = await this.request<{ data: ExtendedCertificate }>(`/api/v1/runs/${runId}/certificate`, {
      method: 'POST',
      headers: {
        ...DEFAULT_HEADERS,
        'X-Device-Key': deviceKey
      },
      body: JSON.stringify({ forceRegenerate })
    });
    return res.data;
  }

  async getPublicCertificate(shareToken: string): Promise<ExtendedCertificate> {
    const res = await this.request<{ data: ExtendedCertificate }>(`/api/v1/certificates/${shareToken}`, {
      method: 'GET',
      headers: DEFAULT_HEADERS
    });
    return res.data;
  }

  private async request<T = unknown>(path: string, init: RequestInit): Promise<T> {
    const url = `${this.baseUrl}${path}`;
    const timeoutMs = 20_000;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const response = await fetch(url, { ...init, signal: controller.signal });
      if (!response.ok) {
        const body = await response.text();
        throw new Error(`API request failed (${response.status}): ${body}`);
      }
      return (await response.json()) as T;
    } catch (error) {
      const name = (error as { name?: string } | null)?.name;
      if (name === 'AbortError') {
        throw new Error(`API request timed out (${timeoutMs}ms): ${url}`);
      }
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  }
}

export interface MinigameResultPayload {
  stepId: string;
  registryId?: string;
  result: Record<string, unknown>;
}

export type FreeInputPayload = {
  stepId: string;
  userText: string;
  routes: { routeKey: string; label?: string }[];
  defaultRouteKey: string;
  emptyRouteKey: string;
  llmTimeoutMs: number;
  suggestedRouteKey?: string;
  inputMeta?: {
    charCount: number;
    isSilent?: boolean;
    isTooShort?: boolean;
    isNonsense?: boolean;
  };
};

export type FreeInputResponse = {
  routeKey: string;
  summaryLabel: string;
  assistantLines: string[];
  write?: { eventId: string; historyIndex: number; recordedAt: string };
};

